import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ScreenConfig } from 'new/routes/RouterManager';
import LoadingProgress from 'js/components/elements/LoadingProgress';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { useSelector } from 'react-redux';
import { getCurrentUser } from 'js/reducers/session';
import { FlowErrorView } from './FlowErrorView';
import { FlowSelectionPage } from 'new/components/integrate/FlowSelectionPage/FlowSelectionPage';
import { useAppContext } from 'new/hooks/useAppContext';

interface Props {
  history: {
    push: (path: string) => void;
  };
}

interface Params {
  flowId: string;
}

const BASE_URL = '/integrate/flows';

function getURL(flow: any, queryString: string) {
  if (!flow) return BASE_URL;

  const { id, flow_type } = flow;

  if (!flow_type || !id) return BASE_URL;

  // Convert backend flow_type to UI route name
  const type = flow_type === 'rag' ? 'genai_rag' : flow_type; // TODO: Use flowTypeMapping.toUIFlowType() when routes are standardized

  return `${BASE_URL}/${type}/${id}/edit${queryString}`;
}

export default function FlowNavigationGuard({ history }: Props) {
  const adapter = usePipelinesAdapter();
  const { search: querySearch, pathname } = useLocation();
  const { isEmbeddedMode } = useAppContext();
  const { flowId } = useParams<Params>();
  const currentUser = useSelector(getCurrentUser);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return; // don't proceed if the user is not logged in
    if (pathname?.includes('select-type')) return;

    // Defensive check for flowId
    if (!flowId || isNaN(Number(flowId))) {
      setError('Invalid flow ID. Please check the URL and try again.');
      setIsLoading(false);
      return;
    }

    // Defensive check for adapter
    if (!adapter || !adapter.getPipelineById) {
      setError('Unable to load flow data. Please try again.');
      setIsLoading(false);
      return;
    }

    (async () => {
      try {
        const flow = await adapter.getPipelineById(Number(flowId));

        // Defensive check for flow response
        if (!flow) {
          setError(
            'Flow not found. The flow may have been deleted or you may not have permission to access it.',
          );
          setIsLoading(false);
          return;
        }

        const targetURL = getURL(flow, querySearch);

        // Defensive check for targetURL
        if (!targetURL || typeof targetURL !== 'string') {
          setError('Unable to determine flow type. Please try again.');
          setIsLoading(false);
          return;
        }

        history.push(targetURL);
      } catch (e) {
        setError('Unable to load flow. Please check your permissions and try again.');
        setIsLoading(false);
      }
    })();
  }, [flowId]);

  if (error) {
    return (
      <FlowErrorView
        error={error}
        flowId={flowId}
        baseUrl={BASE_URL}
        isEmbeddedMode={isEmbeddedMode}
      />
    );
  }

  if (pathname?.includes('select-type')) {
    return <FlowSelectionPage />;
  }

  return (
    <Stack>
      <LoadingProgress isLoading={isLoading} />
    </Stack>
  );
}

FlowNavigationGuard.screenConfig = {
  exact: true,
  pageTitle: 'Flow',
  screenName: 'FlowNavigationGuard',
  path: [`${ROUTE_CONNECT}/flows/:flowId`],
} as ScreenConfig<any>;
